// src/theme/themeTokens.js — 扩展视觉维度 token 系统
// 每个主题不仅控制颜色，还控制字体/形状/边框/阴影/动效/装饰

// ─── 预设模板库 ────────────────────────────────────────

export const SHAPE_PRESETS = {
  rounded: {
    name: '圆润',
    cardRadius: 16,
    buttonRadius: 12,
    pillRadius: 999,
    inputRadius: 12,
  },
  sharp: {
    name: '锐利',
    cardRadius: 4,
    buttonRadius: 4,
    pillRadius: 2,
    inputRadius: 4,
  },
  mixed: {
    name: '混合',
    cardRadius: 8,
    buttonRadius: 20,
    pillRadius: 999,
    inputRadius: 8,
  },
  pill: {
    name: '胶囊',
    cardRadius: 24,
    buttonRadius: 999,
    pillRadius: 999,
    inputRadius: 999,
  },
  retro: {
    name: '复古折角',
    cardRadius: 2,
    buttonRadius: 2,
    pillRadius: 2,
    inputRadius: 2,
  },
};

export const BORDER_PRESETS = {
  solid: { name: '实线', borderStyle: 'solid', borderWidth: 1 },
  subtle: { name: '淡线', borderStyle: 'solid', borderWidth: 0.5 },
  heavy: { name: '粗线', borderStyle: 'solid', borderWidth: 3 },
  dashed: { name: '虚线', borderStyle: 'dashed', borderWidth: 1.5 },
  glow: { name: '发光', borderStyle: 'glow', borderWidth: 2 },
  none: { name: '无边框', borderStyle: null, borderWidth: 0 },
};

export const SHADOW_PRESETS = {
  soft: {
    name: '柔和',
    card: { shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
    button: { shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  },
  glow: {
    name: '霓虹发光',
    card: { shadowOpacity: 0.3, shadowRadius: 16, elevation: 8 },
    button: { shadowOpacity: 0.4, shadowRadius: 10, elevation: 6 },
  },
  hard: {
    name: '硬投影',
    card: { shadowOpacity: 0.15, shadowRadius: 2, elevation: 6 },
    button: { shadowOpacity: 0.2, shadowRadius: 1, elevation: 4 },
  },
  none: {
    name: '无阴影',
    card: { shadowOpacity: 0, shadowRadius: 0, elevation: 0 },
    button: { shadowOpacity: 0, shadowRadius: 0, elevation: 0 },
  },
};

export const MOTION_PRESETS = {
  smooth: { name: '流畅', duration: 200, easing: 'ease-out' },
  sharp: { name: '利落', duration: 100, easing: 'ease-in-out' },
  bouncy: { name: '弹跳', duration: 350, easing: 'spring' },
  none: { name: '无动效', duration: 0, easing: 'linear' },
};

export const DECO_PRESETS = {
  none: { name: '无', type: null, opacity: 0 },
  grid: { name: '网格线', type: 'grid', opacity: 0.06, color: 'rgba(255,255,255,0.5)' },
  halftone: { name: '网点', type: 'halftone', opacity: 0.08, color: 'rgba(0,0,0,0.08)' },
  scanline: { name: '扫描线', type: 'scanline', opacity: 0.04, color: 'rgba(0,0,0,0.04)' },
  sparkle: { name: '星光', type: 'sparkle', opacity: 0.1, color: 'rgba(255,215,0,0.3)' },
  hearts: { name: '爱心', type: 'hearts', opacity: 0.06, color: 'rgba(255,105,180,0.2)' },
  bubbles: { name: '气泡', type: 'bubbles', opacity: 0.08, color: 'rgba(255,255,255,0.3)' },
  inkstone: { name: '墨韵', type: 'inkstone', opacity: 0.06, color: 'rgba(44,36,22,0.06)' },
};

// ─── 构建完整视觉配置 ──────────────────────────────────

export function createVisualConfig({
  shape = 'rounded',
  border = 'solid',
  shadow = 'soft',
  motion = 'smooth',
  decoration = 'none',
  typeface = {},
} = {}) {
  return {
    shape: SHAPE_PRESETS[shape],
    border: BORDER_PRESETS[border],
    shadow: SHADOW_PRESETS[shadow],
    motion: MOTION_PRESETS[motion],
    decoration: DECO_PRESETS[decoration],
    typeface: {
      body: 'Noto Sans SC',
      title: 'Noto Sans SC',
      mono: 'JetBrains Mono',
      ...typeface,
    },
  };
}

// ─── 语义图标映射 ──────────────────────────────────────
// 每个视觉风格将同一个语义键映射到不同的 lucide 图标
// 使用方式: getIcon('search') → mecha风格返回Cpu, kawaii返回Heart

export const ICON_SETS = {
  academic: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Trophy',
    magic: 'Sparkles',
    star: 'Star',
    target: 'Target',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Award',
    brain: 'Brain',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
  mecha: {
    search: 'Cpu',
    study: 'CircuitBoard',
    trophy: 'Trophy',
    magic: 'Zap',
    star: 'Star',
    target: 'Crosshair',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Crown',
    brain: 'Cpu',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Terminal',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Timer',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertTriangle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
  kawaii: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Star',
    magic: 'Sparkles',
    star: 'Heart',
    target: 'Target',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Gift',
    brain: 'Sparkles',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
  anime: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Swords',
    magic: 'Sparkles',
    star: 'Star',
    target: 'Crosshair',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Award',
    brain: 'Brain',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
  retro: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Trophy',
    magic: 'Sparkles',
    star: 'Star',
    target: 'Target',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Award',
    brain: 'Brain',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
  ink_wash: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Trophy',
    magic: 'Wand',
    star: 'Star',
    target: 'Crosshair',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Award',
    brain: 'Brain',
    edit: 'Brush',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'TrendingUp',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'Brush',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'ScrollText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Waves',
    gem: 'Gem',
  },
  minimal: {
    search: 'Search',
    study: 'BookOpen',
    trophy: 'Trophy',
    magic: 'Sparkles',
    star: 'Star',
    target: 'Target',
    check: 'CheckCircle',
    close: 'XCircle',
    plus: 'Plus',
    camera: 'Camera',
    calendar: 'Calendar',
    award: 'Award',
    brain: 'Brain',
    edit: 'FileEdit',
    share: 'Share2',
    filter: 'Filter',
    crown: 'Crown',
    gift: 'Gift',
    chart: 'BarChart3',
    idea: 'Lightbulb',
    calculator: 'Calculator',
    home: 'Home',
    user: 'User',
    settings: 'Settings',
    bell: 'Bell',
    book: 'BookMarked',
    pen: 'PenTool',
    trash: 'Trash2',
    message: 'MessageCircle',
    school: 'GraduationCap',
    timer: 'Clock',
    lock: 'Lock',
    unlock: 'Unlock',
    image: 'Image',
    file: 'FileText',
    music: 'Music',
    video: 'Video',
    heart: 'Heart',
    compass: 'Compass',
    mapPin: 'MapPin',
    tag: 'Tag',
    bookmark: 'Bookmark',
    flag: 'Flag',
    thumbsUp: 'ThumbsUp',
    alert: 'AlertCircle',
    help: 'HelpCircle',
    info: 'Info',
    external: 'ExternalLink',
    more: 'MoreHorizontal',
    menu: 'Menu',
    arrowRight: 'ArrowRight',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown',
    refresh: 'RefreshCw',
    download: 'Download',
    upload: 'Upload',
    send: 'Send',
    back: 'ChevronLeft',
    forward: 'ChevronRight',
    expand: 'ChevronDown',
    collapse: 'ChevronUp',
    palette: 'Palette',
    zap: 'Zap',
    flame: 'Flame',
    sun: 'Sun',
    moon: 'Moon',
    leaf: 'Leaf',
    waves: 'Droplets',
    gem: 'Gem',
  },
};

// ─── 预设视觉风格包 ─────────────────────────────────────

export const VISUAL_STYLE_PRESETS = {
  academic: createVisualConfig({ shape: 'rounded', border: 'solid', shadow: 'soft', motion: 'smooth', decoration: 'none' }),
  mecha: createVisualConfig({ shape: 'sharp', border: 'glow', shadow: 'glow', motion: 'sharp', decoration: 'grid',
    typeface: { body: 'Noto Sans SC', title: 'Rajdhani', mono: 'JetBrains Mono' } }),
  kawaii: createVisualConfig({ shape: 'pill', border: 'dashed', shadow: 'none', motion: 'bouncy', decoration: 'hearts',
    typeface: { body: 'Noto Sans SC', title: 'Noto Sans SC', mono: 'JetBrains Mono' } }),
  anime: createVisualConfig({ shape: 'mixed', border: 'heavy', shadow: 'hard', motion: 'bouncy', decoration: 'halftone',
    typeface: { body: 'Noto Sans SC', title: 'Noto Sans SC', mono: 'JetBrains Mono' } }),
  retro: createVisualConfig({ shape: 'retro', border: 'heavy', shadow: 'hard', motion: 'sharp', decoration: 'scanline' }),
  minimal: createVisualConfig({ shape: 'mixed', border: 'none', shadow: 'none', motion: 'none', decoration: 'none' }),
  ink_wash: createVisualConfig({
    shape: 'retro',
    border: 'subtle',
    shadow: 'soft',
    motion: 'smooth',
    decoration: 'inkstone',
    typeface: { body: 'Noto Serif SC', title: 'Noto Serif SC', mono: 'JetBrains Mono' },
  }),
};
